import {
  ChangeEvent as ReactChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import styled, { css } from "styled-components";
import { debounce } from "lodash-es";
import { SmallPlainIcon } from "../Icon/PlainIcon";
import { DropdownMenu } from "../DropdownMenu";
import { BaseComponentProps } from "../types";
import { SearchScope } from "./types";
import { expandIconAnimation } from "../expandIconAnimation";
import {
  primaryActive,
  primaryDisabled,
  primaryInactive,
} from "../../typography/alpha";

type StyledSearchContainerProps = {
  disabled?: boolean;
};

const StyledSearchContainer = styled.div<StyledSearchContainerProps>`
  position: relative;
  display: flex;
  box-sizing: border-box;
  height: 32px;
  border: 1px solid var(--color-primary-400);
  border-radius: var(--size-border-radius-small);
  :focus-within {
    outline: none;
  }
  ${(props) =>
    props.disabled
      ? css`
          border: 1px solid var(--color-primary-200);
          input::placeholder {
            color: var(--color-primary-200);
          }
        `
      : css`
          &:hover {
            border: 1px solid var(--color-primary-800);
          }
          &:focus-within {
            border: 1px solid var(--color-secondary-800);
          }
        `}
`;

const StyledSearchIconContainer = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  right: 12px;
  bottom: 0;
`;

type StyledScopeSelectorProps = {
  disabled?: boolean;
  expanded: boolean;
};

const StyledScopeSelector = styled.div<StyledScopeSelectorProps>`
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 20ch;
  overflow: hidden;
  text-overflow: ellipsis;
  background: var(--color-secondary-200);
  border-top-left-radius: var(--size-border-radius-small);
  border-bottom-left-radius: var(--size-border-radius-small);
  padding: 0px 8px;
  line-height: 16px;
  ${(props) => (props.disabled ? primaryDisabled.body3 : primaryActive.body3)};

  > :first-child {
    margin-right: 8px;
  }

  ${expandIconAnimation}
`;

const ScopeLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ScopeList = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0px;
  z-index: 1;
`;

type useCloseIfDisabledArgs = {
  disabled?: boolean;
  setExpanded: (value: boolean) => void;
};

const useCloseIfDisabled = ({
  disabled,
  setExpanded,
}: useCloseIfDisabledArgs) => {
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (disabled) {
      setExpanded(false);
    }
  }, [disabled]);
  /* eslint-enable react-hooks/exhaustive-deps */
};

type ScopeSelectorProps = Pick<BaseSearchProps, "disabled" | "scopes"> & {
  selectedScope: SearchScope;
  setSelectedScope: (scope: SearchScope) => void;
  performSearch: (
    searchText: string,
    scope?: { id: string; label: string }
  ) => void;
  searchText: string;
};

const ScopeSelector = ({
  disabled,
  scopes,
  selectedScope,
  setSelectedScope,
  performSearch,
  searchText,
}: ScopeSelectorProps) => {
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = disabled
    ? () => {}
    : () => {
        setExpanded((prevExpanded) => !prevExpanded);
      };

  const closeList = disabled
    ? () => {}
    : () => {
        setExpanded(false);
      };

  const setScope = (scope: SearchScope) => {
    setSelectedScope(scope);
    performSearch(searchText, scope);
  };

  useCloseIfDisabled({ disabled, setExpanded });

  return (
    <>
      <StyledScopeSelector
        disabled={disabled}
        expanded={expanded}
        onClick={toggleExpanded}
      >
        <ScopeLabel>{selectedScope.label}</ScopeLabel>
        <SmallPlainIcon
          iconName="chevron-down"
          variant={disabled ? "DISABLED" : "DEFAULT"}
        />
      </StyledScopeSelector>
      {expanded && (
        <ScopeList>
          <DropdownMenu>
            {scopes.map((scope) => (
              <li
                key={scope.id}
                onClick={() => {
                  setScope(scope);
                  closeList();
                }}
              >
                {scope.label}
              </li>
            ))}
          </DropdownMenu>
        </ScopeList>
      )}
    </>
  );
};

const StyledSearch = styled.input`
  display: block;
  min-width: 0px;
  flex-grow: 1;
  padding: 0px 12px;
  padding-right: 34px;
  background: var(--color-neutral-000-full);
  border: none;
  border-radius: var(--size-border-radius-small);
  ${primaryActive.body3};

  :focus {
    outline: none;
  }

  ::placeholder {
    ${primaryInactive.body3};
  }
`;

const StyledSmallPlainIcon = styled(SmallPlainIcon)`
  cursor: pointer;
`;

type useSearchArgs = {
  scopes: BaseSearchProps["scopes"];
  onSearch: BaseSearchProps["onSearch"];
  incrementalSearch: BaseSearchProps["incrementalSearch"];
  onKeyEnter?<P extends unknown[]>(args?: P): void;
  value: string;
  debounceTime?: number;
};

const useSearch = ({
  onSearch,
  scopes,
  incrementalSearch,
  onKeyEnter,
  debounceTime = 1000,
  value,
}: useSearchArgs) => {
  const [selectedScope, setSelectedScope] = useState(scopes[0]);
  const [searchText, setSearchText] = useState(value);

  useEffect(() => {
    if (value !== searchText) {
      setSearchText(value);
      performSearch(value);
    }
    // eslint-disable-next-line
  }, [value]);

  const clearSearchText = () => {
    performSearch("");
    setSearchText("");
  };
  const performSearch = useCallback(
    (searchTerm, scope = selectedScope) => {
      if (scope) setSelectedScope(scope);

      onSearch({ searchText: searchTerm, scope });
    },
    [onSearch, selectedScope]
  );
  const debouncedPerformSearch = useMemo(
    () => debounce(performSearch, debounceTime),
    [performSearch, debounceTime]
  );
  const inputChangeHandler = (e: ReactChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    if (incrementalSearch) {
      debouncedPerformSearch(e.target.value);
    }
  };
  const inputKeypressHandler = (e: ReactKeyboardEvent) => {
    if (e.key === "Enter") {
      onKeyEnter ? onKeyEnter() : performSearch(searchText);
    }
  };

  return {
    searchText,
    clearSearchText,
    selectedScope,
    setSelectedScope,
    inputChangeHandler,
    inputKeypressHandler,
    performSearch,
  };
};

type onSearchArgs = {
  searchText: string;
  scope: SearchScope;
};

type BaseSearchProps = BaseComponentProps<
  "input",
  {
    className?: string;
    testId?: string;
    value?: string;
    placeholder: string;
    /** Time for debounce delay. Default is 1000ms */
    debounceTime?: number;
    /** List of search scopes to populate the scopes dropdown. */
    scopes: SearchScope[];
    /** Function called when the user hits the "Enter" key in the search input. The selected scope will be passed along with the search text. */
    onSearch: ({ searchText, scope }: onSearchArgs) => void;
    /** Boolean to turn on/off search as you type. */
    incrementalSearch?: boolean;
    /* Optional and specific enter key press handler */
    onKeyEnter?<P extends unknown[]>(args?: P): void;
  }
>;

export const BaseSearch = ({
  scopes,
  value = "",
  debounceTime,
  onSearch,
  incrementalSearch,
  onKeyEnter,
  className,
  testId,
  id,
  ...inputProps
}: BaseSearchProps) => {
  const {
    searchText,
    clearSearchText,
    selectedScope,
    setSelectedScope,
    inputChangeHandler,
    inputKeypressHandler,
    performSearch,
  } = useSearch({
    onSearch,
    scopes,
    incrementalSearch,
    onKeyEnter,
    debounceTime,
    value,
  });

  return (
    <StyledSearchContainer
      className={className}
      disabled={inputProps.disabled}
      data-testid={testId}
      id={id}
    >
      {scopes.length > 0 && (
        <ScopeSelector
          disabled={inputProps.disabled}
          scopes={scopes}
          selectedScope={selectedScope}
          setSelectedScope={setSelectedScope}
          performSearch={performSearch}
          searchText={searchText}
        />
      )}
      <StyledSearch
        disabled={inputProps.disabled}
        {...inputProps}
        value={searchText}
        onChange={inputChangeHandler}
        onKeyPress={inputKeypressHandler}
      />
      <StyledSearchIconContainer>
        {searchText.length > 0 ? (
          <StyledSmallPlainIcon iconName="close" onClick={clearSearchText} />
        ) : (
          <SmallPlainIcon
            iconName="search"
            variant={inputProps.disabled ? "DISABLED" : "DEFAULT"}
          />
        )}
      </StyledSearchIconContainer>
    </StyledSearchContainer>
  );
};
